import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { useLogger } from '@internal/ui-common';
import { resolveLocalRefs } from '../utils/resolveJsonRefs';
import { transformAsyncApiSpecificationsToMessages } from '../utils/messageHelpers';
import type { IAsyncApiSpecification } from '@internal/schemas-common';
import type { IMessage } from '../schemas/IMessage.interface';

export const useAsyncApiMessages = () => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const logger = useLogger();

  const [messages, setMessages] = useState<IMessage[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const baseUrl = await discoveryApi.getBaseUrl('com-zf-apispecfetcher');
        const response = await fetchApi.fetch(
          `${baseUrl}/all-async-api-specifications`,
          {
            signal,
          },
        );

        if (!response.ok)
          throw new Error(
            `API error: ${response.status} ${response.statusText}`,
          );

        // It is not typesafe, but as we do control the backend, extra checks are not necessary
        const rawSpecifications =
          (await response.json()) as IAsyncApiSpecification[];

        const resolvedSpecifications = rawSpecifications.map(specification => ({
          ...specification,
          specification: resolveLocalRefs(specification.specification),
        }));

        const transformedMessages = transformAsyncApiSpecificationsToMessages(
          resolvedSpecifications,
        );
        setMessages(transformedMessages);
      } catch (catchError) {
        if (signal.aborted) return;
        logger.error('Error fetching AsyncAPI messages', catchError);
        setError(new Error('Failed to load AsyncAPI messages.'));
        setMessages([]);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchData();

    return () => controller.abort();
  }, [discoveryApi, fetchApi, logger]);

  return {
    messages,
    isLoading,
    error,
  };
};
